import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IncomeType } from '@shared/components/income-types-combobox/income-type';
import { IncomeTypesComboboxComponent } from '@shared/components/income-types-combobox/income-types-combobox.component';
import { InvestmentJurisdictionComboboxComponent } from '@shared/components/investment-jurisdiction-combobox/investment-jurisdiction-combobox.component';
import { SecurityTypesComboboxComponent } from '@shared/components/security-types-combobox/security-types-combobox.component';
import { ModalRef } from '@shared/models/modal-ref';
import { RichEditorService } from '@shared/modules/rich-editor/services/rich-editor.service';
import { DialogFactoryService } from '@shared/services/dialog-factory.service';
import { RichEdit } from 'devexpress-richedit';
import {
  EntityType,
  GeneralRatesRuleEngine,
  GridDrawerComponent,
  SecurityType,
} from '../../models';
import { InvestmentJurisdictionShort } from '../../models/investment-jurisdiction-short';
import { AdminExplanationEditorComponent } from '../explanation-editor/explanation-editor.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-upsert-general-rates-rule-engine',
  templateUrl: 'upsert-general-rates-rule-engine.component.html',
  styleUrls: ['./upsert-general-rates-rule-engine.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class UpsertGeneralRatesRuleEngineComponent
  extends GridDrawerComponent<GeneralRatesRuleEngine>
  implements OnChanges, OnInit
{
  private static readonly RATE_PATTERN = /^\d+(\.\d+)?$/;
  private static readonly RATE_MAX = 999;
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private dialogFactoryService = inject(DialogFactoryService);
  private richEditorService = inject(RichEditorService);

  private explanationEditorRef?: ModalRef<AdminExplanationEditorComponent>;
  public explanationHtml = '';

  public form: FormGroup = this.createForm();
  public selectedInvestmentJurisdictionId: number | null = null;
  public selectedSecurityTypeId: number | null = null;
  public selectedIncomeTypeId: number | null = null;
  public selectedRate: number | null = 0;
  public ratesList: number[] = [];
  public editingRateIndex: number | null = null;
  public selectedEntityTypeValue: EntityType | null = null;
  public selectedInvestmentJurisdictionName = '';
  public selectedSecurityTypeName = '';
  public selectedIncomeTypeName = '';
  public selectedInvestmentJurisdictionValue: InvestmentJurisdictionShort | null =
    null;
  public selectedSecurityTypeValue: SecurityType | null = null;
  public selectedIncomeTypeValue: IncomeType | null = null;
  public explanationMaxLength = 5000;
  public allowedSecurityTypeNames: string[] = [
    'Corporate Debt',
    'Government Debt',
    'Listed Equity',
    'Unlisted Equity',
  ]; // Manually selected the availables security types.

  @Input() isBulkEdit = false;
  @Input() bulkItems: GeneralRatesRuleEngine[] = [];

  @ViewChild('investmentJurisdictionComp')
  investmentJurisdictionComp!: InvestmentJurisdictionComboboxComponent;

  @ViewChild('securityTypesComp')
  securityTypesComp!: SecurityTypesComboboxComponent;

  @ViewChild('incomeTypesComp')
  incomeTypesComp!: IncomeTypesComboboxComponent;

  get showIncomeTypeSection(): boolean {
    const securityTypeIds = this.form.get('securityTypeIds')?.value as
      | number[]
      | null
      | undefined;
    return (
      !this.isBulkEdit &&
      (!!this.selectedSecurityTypeId || !!securityTypeIds?.length)
    );
  }

  get showRateAndExplanationSection(): boolean {
    return (
      this.isBulkEdit ||
      (this.showIncomeTypeSection &&
        !!this.selectedIncomeTypeValue?.taxResultItemTypeId)
    );
  }

  ngOnInit(): void {
    this.applyIncomeTypeDependentValidators();
    this.setupFormValueChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isBulkEdit']) {
      if (this.isBulkEdit) {
        this.form.get('entityTypeId')?.clearValidators();
        this.form.get('entityTypeId')?.updateValueAndValidity({
          emitEvent: false,
        });
      } else {
        this.form.get('entityTypeId')?.setValidators(Validators.required);
        this.form.get('entityTypeId')?.updateValueAndValidity({
          emitEvent: false,
        });
      }

      this.applyIncomeTypeDependentValidators();
    }

    if (changes['model']?.currentValue) {
      const model: GeneralRatesRuleEngine = this
        .model as GeneralRatesRuleEngine;
      this.selectedEntityTypeValue = model.entityTypeId
        ? {
            entityTypeId: model.entityTypeId,
            name: model.entityTypeName || `${model.entityTypeId}`,
            description: '',
            isDefault: false,
            isActive: true,
            country: model.country,
            entityTypeInvestmentJurisdiction: [],
          }
        : null;
      this.selectedInvestmentJurisdictionId =
        model.investmentJurisdictionIds?.[0] ?? null;
      this.selectedSecurityTypeId = model.securityTypeIds?.[0] ?? null;
      this.selectedIncomeTypeId = model.incomeTypeId ?? null;
      this.selectedRate = model.rate ?? 0;
      this.ratesList = model.rates?.length
        ? [...model.rates]
        : model.rate !== undefined && model.rate !== null
          ? [model.rate]
          : [];
      this.selectedInvestmentJurisdictionName =
        model.investmentJurisdictionNames?.[0] ?? '';
      this.selectedSecurityTypeName = model.securityTypeNames?.[0] ?? '';
      this.selectedIncomeTypeName = model.incomeTypeName ?? '';
      this.selectedInvestmentJurisdictionValue = this
        .selectedInvestmentJurisdictionId
        ? {
            investmentJurisdictionId: this.selectedInvestmentJurisdictionId,
            name:
              this.selectedInvestmentJurisdictionName ||
              `${this.selectedInvestmentJurisdictionId}`,
          }
        : null;
      this.selectedSecurityTypeValue = this.selectedSecurityTypeId
        ? {
            securityTypeId: this.selectedSecurityTypeId,
            name:
              this.selectedSecurityTypeName || `${this.selectedSecurityTypeId}`,
            isActive: true,
            created: new Date(0),
            isParent: false,
            parentName: '',
          }
        : null;
      this.selectedIncomeTypeValue = this.selectedIncomeTypeId
        ? {
            taxResultItemTypeId: this.selectedIncomeTypeId,
            name: this.selectedIncomeTypeName || `${this.selectedIncomeTypeId}`,
          }
        : null;

      this.form.patchValue(
        {
          entityTypeId: model.entityTypeId,
          investmentJurisdictionIds: this.selectedInvestmentJurisdictionId
            ? [this.selectedInvestmentJurisdictionId]
            : [],
          securityTypeIds: this.selectedSecurityTypeId
            ? [this.selectedSecurityTypeId]
            : [],
          incomeTypeId: this.selectedIncomeTypeId,
          rate: this.isBulkEdit
            ? null
            : this.ratesList.length > 0
              ? null
              : this.selectedRate,
          explanation: this.isBulkEdit ? '' : (model.explanation ?? ''),
        },
        { emitEvent: false }
      );

      if (this.isBulkEdit) {
        this.selectedRate = null;
        this.ratesList = [];
        this.editingRateIndex = null;
        this.explanationHtml = '';
      } else {
        this.explanationHtml = model.explanationHtml ?? model.explanation ?? '';
      }

      this.applyIncomeTypeDependentValidators();

      // Keep edit mode disabled until the user actually changes a value.
      this.form.markAsPristine();
      this.form.markAsUntouched();
      this.cdr.markForCheck();
    }
  }

  addInvestmentJurisdiction(
    investmentJurisdiction: InvestmentJurisdictionShort | null | undefined
  ): void {
    const jurisdictionId = investmentJurisdiction?.investmentJurisdictionId;
    this.selectedInvestmentJurisdictionId = jurisdictionId ?? null;
    this.selectedInvestmentJurisdictionName =
      investmentJurisdiction?.name ?? '';
    this.selectedInvestmentJurisdictionValue = investmentJurisdiction ?? null;
    this.form.patchValue({
      investmentJurisdictionIds: this.selectedInvestmentJurisdictionId
        ? [this.selectedInvestmentJurisdictionId]
        : [],
    });
  }

  addSecurityType(securityType: SecurityType | null | undefined): void {
    const securityTypeId = securityType?.securityTypeId;
    const hasSecurityTypeChanged =
      this.selectedSecurityTypeId !== securityTypeId;

    this.selectedSecurityTypeId = securityTypeId ?? null;
    this.selectedSecurityTypeName = securityType?.name ?? '';
    this.selectedSecurityTypeValue = securityType ?? null;

    if (hasSecurityTypeChanged) {
      this.clearIncomeType();
    }

    this.form.patchValue({
      securityTypeIds: this.selectedSecurityTypeId
        ? [this.selectedSecurityTypeId]
        : [],
    });

    this.applyIncomeTypeDependentValidators();
    this.cdr.markForCheck();
  }

  addIncomeType(incomeType: IncomeType | number | null | undefined): void {
    const incomeTypeId =
      typeof incomeType === 'number'
        ? incomeType
        : (incomeType?.taxResultItemTypeId ?? null);

    const incomeTypeName =
      typeof incomeType === 'number' ? '' : (incomeType?.name ?? '');

    this.selectedIncomeTypeId = incomeTypeId;
    this.selectedIncomeTypeName = incomeTypeName;
    this.selectedIncomeTypeValue = incomeTypeId
      ? {
          taxResultItemTypeId: incomeTypeId,
          name: incomeTypeName || `${incomeTypeId}`,
        }
      : null;

    this.form.patchValue({
      incomeTypeId: this.selectedIncomeTypeId,
    });

    this.applyIncomeTypeDependentValidators();

    if (!this.selectedIncomeTypeId && !this.isBulkEdit) {
      this.form.patchValue({
        rate: 0,
        explanation: '',
      });
      this.explanationHtml = '';
    }

    this.cdr.markForCheck();
  }

  selectEntityType(entityType: EntityType | null | undefined): void {
    this.selectedEntityTypeValue = entityType ?? null;
    this.form.patchValue({
      entityTypeId: entityType?.entityTypeId ?? null,
    });
  }

  clearEntityType(): void {
    this.selectedEntityTypeValue = null;
    this.form.patchValue({
      entityTypeId: null,
    });
  }

  clearInvestmentJurisdiction(): void {
    this.selectedInvestmentJurisdictionId = null;
    this.selectedInvestmentJurisdictionName = '';
    this.selectedInvestmentJurisdictionValue = null;
    this.form.patchValue({
      investmentJurisdictionIds: [],
    });
  }

  clearSecurityType(): void {
    this.selectedSecurityTypeId = null;
    this.selectedSecurityTypeName = '';
    this.selectedSecurityTypeValue = null;
    this.form.patchValue({
      securityTypeIds: [],
    });

    this.clearIncomeType();
  }

  clearIncomeType(): void {
    this.selectedIncomeTypeId = null;
    this.selectedIncomeTypeName = '';
    this.selectedIncomeTypeValue = null;
    this.form.patchValue({
      incomeTypeId: null,
      rate: this.isBulkEdit ? null : 0,
      explanation: '',
    });
    this.explanationHtml = '';

    this.selectedRate = this.isBulkEdit ? null : 0;
    this.ratesList = [];
    this.editingRateIndex = null;
    this.applyIncomeTypeDependentValidators();
    this.cdr.markForCheck();
  }

  selectRateForEdit(index: number): void {
    this.editingRateIndex = index;
    this.form.patchValue({ rate: this.ratesList[index] }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  openExplanationEditor(): void {
    this.explanationEditorRef = this.dialogFactoryService.open(
      this.translateService.instant('admin.generalRatesRuleEngine.explanation'),
      AdminExplanationEditorComponent,
      {
        data: {
          title: this.translateService.instant(
            'admin.generalRatesRuleEngine.explanation'
          ),
          content:
            this.explanationHtml || this.form.get('explanation')?.value || '',
        },
      }
    );

    this.explanationEditorRef
      .afterConfirm<RichEdit | undefined>()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(richEditor => {
        if (richEditor) {
          this.richEditorService.exportToHtml(richEditor, content => {
            this.explanationHtml = this.extractBodyHtml(content);
            this.form.patchValue({
              explanation: this.extractPlainTextFromHtml(this.explanationHtml),
            });
            this.cdr.markForCheck();
          });
        }
      });
  }

  private extractBodyHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.innerHTML?.trim() ?? '';
  }

  private extractPlainTextFromHtml(html: string): string {
    if (!html || !/<[^>]+>/.test(html)) {
      return html ?? '';
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const root = doc.body;

    const toText = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? '';
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }

      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (tag === 'li') {
        const itemText = Array.from(el.childNodes).map(toText).join('').trim();
        return itemText ? `- ${itemText}\n` : '';
      }

      const text = Array.from(el.childNodes).map(toText).join('');

      if (tag === 'p' || tag === 'div' || tag === 'br') {
        return `${text}\n`;
      }

      if (tag === 'ul' || tag === 'ol') {
        return `${text}\n`;
      }

      return text;
    };

    return Array.from(root.childNodes)
      .map(toText)
      .join('')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private resolveExplanationForSave(textValue: string): string {
    const currentText = textValue ?? '';
    if (!this.explanationHtml) {
      return currentText;
    }

    const htmlAsText = this.extractPlainTextFromHtml(this.explanationHtml);
    const normalize = (value: string): string =>
      value.replace(/\r\n/g, '\n').trim();

    return normalize(currentText) === normalize(htmlAsText)
      ? this.explanationHtml
      : currentText;
  }

  addRateToList(event: Event): void {
    event.preventDefault();
    const value = this.form.get('rate')?.value;
    if (value === null || value === undefined || value === '') return;
    const num = Number(value);
    if (
      isNaN(num) ||
      num < 0 ||
      num > UpsertGeneralRatesRuleEngineComponent.RATE_MAX
    )
      return;

    this.saveRate(num);
    this.form.patchValue({ rate: null }, { emitEvent: false });
    this.applyIncomeTypeDependentValidators();
    this.emitCurrentModel();
    this.cdr.markForCheck();
  }

  preventInvalidRateKeys(event: KeyboardEvent): void {
    const allowedControlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Enter',
    ];

    if (allowedControlKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const isDigit = /^\d$/.test(event.key);
    const isDot = event.key === '.';
    const input = event.target as HTMLInputElement | null;
    const hasDot = input?.value.includes('.') ?? false;

    if (isDigit || (isDot && !hasDot)) {
      return;
    }

    event.preventDefault();
  }

  sanitizeRateInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const digitsAndDotsOnly = input.value.replace(/[^\d.]/g, '');
    const [wholePart = '', ...decimalParts] = digitsAndDotsOnly.split('.');
    let sanitized =
      decimalParts.length > 0
        ? `${wholePart}.${decimalParts.join('')}`
        : wholePart;

    if (sanitized) {
      const numericValue = Number(sanitized);
      if (
        !isNaN(numericValue) &&
        numericValue > UpsertGeneralRatesRuleEngineComponent.RATE_MAX
      ) {
        sanitized = `${UpsertGeneralRatesRuleEngineComponent.RATE_MAX}`;
      }
    }

    if (sanitized !== input.value) {
      input.value = sanitized;
      this.form.get('rate')?.setValue(sanitized);
    }
  }

  removeRateFromList(index: number): void {
    this.ratesList = this.ratesList.filter((_, i) => i !== index);
    this.applyIncomeTypeDependentValidators();
    this.emitCurrentModel();
    this.cdr.markForCheck();
  }

  private saveRate(num: number): void {
    if (this.editingRateIndex !== null) {
      const updated = [...this.ratesList];
      updated[this.editingRateIndex] = num;
      this.ratesList = [...new Set(updated)].sort((a, b) => a - b);
      this.editingRateIndex = null;
    } else {
      this.ratesList = [...new Set([...this.ratesList, num])].sort(
        (a, b) => a - b
      );
    }
  }

  private applyPendingRateEdit(): void {
    const value = this.form.get('rate')?.value;
    if (value === null || value === undefined || value === '') return;

    const num = Number(value);
    if (
      isNaN(num) ||
      num < 0 ||
      num > UpsertGeneralRatesRuleEngineComponent.RATE_MAX
    )
      return;

    this.saveRate(num);
    this.form.patchValue({ rate: null }, { emitEvent: false });
  }

  private getEffectiveRates(rateValue: number | null): number[] {
    if (rateValue === null || rateValue === undefined) {
      return [...this.ratesList];
    }

    const num = Number(rateValue);
    if (
      isNaN(num) ||
      num < 0 ||
      num > UpsertGeneralRatesRuleEngineComponent.RATE_MAX
    ) {
      return [...this.ratesList];
    }

    if (this.editingRateIndex !== null) {
      const updated = [...this.ratesList];
      if (
        this.editingRateIndex >= 0 &&
        this.editingRateIndex < updated.length
      ) {
        updated[this.editingRateIndex] = num;
      }
      return [...new Set(updated)].sort((a, b) => a - b);
    }

    return [...new Set([...this.ratesList, num])].sort((a, b) => a - b);
  }

  private setupFormValueChanges(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (values: {
          entityTypeId: number;
          investmentJurisdictionIds: number[];
          securityTypeIds: number[];
          incomeTypeId: number | null;
          rate: number | null;
          explanation: string;
        }) => {
          const effectiveRates = this.getEffectiveRates(values.rate);
          const isValid = this.form.valid && this.isRequiredFieldsValid();

          const modelData: GeneralRatesRuleEngine = {
            generalRatesRuleEngineId: this.model?.generalRatesRuleEngineId || 0,
            entityTypeId: values.entityTypeId,
            generalRateRulesEditorRateId:
              this.model?.generalRateRulesEditorRateId,
            rate:
              effectiveRates.length > 0
                ? effectiveRates[0]
                : (values.rate ?? undefined),
            rates: effectiveRates.length > 0 ? [...effectiveRates] : undefined,
            investmentJurisdictionIds: values.investmentJurisdictionIds,
            securityTypeIds: values.securityTypeIds,
            incomeTypeId: values.incomeTypeId ?? undefined,
            incomeTypeName: this.selectedIncomeTypeName,
            explanation: this.resolveExplanationForSave(values.explanation),
          };

          this.modelChange.emit({
            model: modelData,
            prevModel: this.model!,
            isValid,
          });
        }
      );
  }

  private isRequiredFieldsValid(): boolean {
    if (this.isBulkEdit) {
      return true;
    }

    return (
      !!this.selectedInvestmentJurisdictionId &&
      !!this.selectedSecurityTypeId &&
      !!this.selectedIncomeTypeId
    );
  }

  private applyIncomeTypeDependentValidators(): void {
    const explanationControl = this.form.get('explanation');
    const rateControl = this.form.get('rate');
    const incomeTypeId =
      (this.form.get('incomeTypeId')?.value as number | null | undefined) ??
      this.selectedIncomeTypeId;
    const securityTypeIds = this.form.get('securityTypeIds')?.value as
      | number[]
      | null
      | undefined;
    const hasSecurityType =
      !!this.selectedSecurityTypeId || !!securityTypeIds?.length;

    if (!explanationControl || !rateControl) {
      return;
    }

    const hasIncomeTypeContext =
      !this.isBulkEdit && hasSecurityType && !!incomeTypeId;

    if (hasIncomeTypeContext) {
      explanationControl.setValidators([
        Validators.required,
        Validators.maxLength(this.explanationMaxLength),
      ]);
      rateControl.setValidators(
        this.ratesList.length === 0
          ? [
              Validators.required,
              Validators.pattern(
                UpsertGeneralRatesRuleEngineComponent.RATE_PATTERN
              ),
              Validators.min(0),
              Validators.max(UpsertGeneralRatesRuleEngineComponent.RATE_MAX),
            ]
          : [
              Validators.pattern(
                UpsertGeneralRatesRuleEngineComponent.RATE_PATTERN
              ),
              Validators.min(0),
              Validators.max(UpsertGeneralRatesRuleEngineComponent.RATE_MAX),
            ]
      );
    } else {
      explanationControl.setValidators([
        Validators.maxLength(this.explanationMaxLength),
      ]);
      rateControl.setValidators([
        Validators.pattern(UpsertGeneralRatesRuleEngineComponent.RATE_PATTERN),
        Validators.min(0),
        Validators.max(UpsertGeneralRatesRuleEngineComponent.RATE_MAX),
      ]);
    }

    explanationControl.updateValueAndValidity({ emitEvent: false });
    rateControl.updateValueAndValidity({ emitEvent: false });
  }

  private emitCurrentModel(): void {
    this.applyPendingRateEdit();
    const values = this.form.value;
    const isValid = this.form.valid && this.isRequiredFieldsValid();

    const modelData: GeneralRatesRuleEngine = {
      generalRatesRuleEngineId: this.model?.generalRatesRuleEngineId || 0,
      entityTypeId: values.entityTypeId,
      generalRateRulesEditorRateId: this.model?.generalRateRulesEditorRateId,
      rate:
        this.ratesList.length > 0
          ? this.ratesList[0]
          : (values.rate ?? undefined),
      rates: this.ratesList.length > 0 ? [...this.ratesList] : undefined,
      investmentJurisdictionIds: values.investmentJurisdictionIds,
      securityTypeIds: values.securityTypeIds,
      incomeTypeId: values.incomeTypeId ?? undefined,
      incomeTypeName: this.selectedIncomeTypeName,
      explanation: this.resolveExplanationForSave(values.explanation),
    };

    this.modelChange.emit({
      model: modelData,
      prevModel: this.model!,
      isValid,
    });
  }

  private createForm(): FormGroup {
    return this.fb.group({
      entityTypeId: [null, Validators.required],
      investmentJurisdictionIds: [[]],
      securityTypeIds: [[]],
      incomeTypeId: [null],
      rate: [
        0,
        [
          Validators.pattern(
            UpsertGeneralRatesRuleEngineComponent.RATE_PATTERN
          ),
          Validators.min(0),
          Validators.max(UpsertGeneralRatesRuleEngineComponent.RATE_MAX),
        ],
      ],
      explanation: ['', [Validators.maxLength(this.explanationMaxLength)]],
    });
  }
}
